import { IonInfiniteScroll } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'

import { usePortalSearchUnitsInfinite } from '@apps-common/apis/portal/unit'
import { UnitListCardSmSkeleton } from '@apps-common/apps/portal/pages/units/components'
import { PortalUnitListCard, PortalUnitsFilterButton } from '@apps-common/apps/portal/pages/units/components/portal'
import { InfiniteLoader, NoResultsCard } from '@apps-common/components/common'
import {
  ModalBodyInfiniteScrollable,
  ModalCloseButton,
  ModalContainer,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { GRID_CARDS_VARIANTS } from '@apps-common/config/motion-variants'
import { usePortalUnitsFilterStore } from '@apps-common/stores'
import { IPortalUnitFilterDefaults, IPortalUnitSearchModel } from '@apps-common/types/portal/unit'
import { ButtonRoundedSm } from '../elements'

export interface IPortalUnitSelectModalProps {
  title?: string
  exclusions?: {
    ids: string[]
    component: any
  }
  replacementButtonComponent?: (
    props: IPortalUnitSearchModel,
    onDismiss: (data?: string | null | undefined | number, role?: string) => void
  ) => any
  filterDefaults?: IPortalUnitFilterDefaults
}

export const PortalUnitSelectModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IPortalUnitSelectModalProps
}) => {
  const [enabled, setEnabled] = useState<boolean>(false)
  const { title, exclusions, replacementButtonComponent, filterDefaults } = componentProps || {}

  const filter = usePortalUnitsFilterStore()
  const {
    isLoading: unitsIsLoading,
    isFetching: unitsIsFetching,
    isRefetching: unitsIsRefetching,
    isError: unitsIsError,
    data: units,
    refetch: unitsRefetch,
    fetchNextPage: unitsFetchNext
  } = usePortalSearchUnitsInfinite({ enabled, model: filter })

  useEffect(() => {
    if (filterDefaults) {
      filter.setTempState(filterDefaults.state)
    }
    setEnabled(true)
  }, [])

  const reducedData = useMemo(() => {
    return units?.pages.reduce((acc: IPortalUnitSearchModel[], curr: IPortalUnitSearchModel[]) => [...acc, ...curr], []) || []
  }, [units])

  const loading = unitsIsLoading || unitsIsRefetching || unitsIsError || !units

  const buttonReplacement = (props: IPortalUnitSearchModel) => {
    if (exclusions?.ids?.includes(props.unitId)) {
      return exclusions?.component
    }

    if (replacementButtonComponent) {
      return replacementButtonComponent(props, onDismiss)
    }

    return (
      <ButtonRoundedSm
        type='button'
        loading={loading}
        text='Select'
        icon={<commonIcons.arrowRight />}
        iconRight
        className='w-full action-brand'
        callback={() => onDismiss(JSON.stringify(props), ModalDismiss.Confirm)}
      />
    )
  }

  return (
    <ModalContainer alwaysMobile innerClasses='!min-h-[95lvh] !max-w-[800px]'>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle='Selection' title={title || 'Devices'} />
          <div className='flex items-center justify-end gap-[5px]'>
            <PortalUnitsFilterButton defaults={filterDefaults} />
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBodyInfiniteScrollable classes='!pt-[120px]'>
        <AnimatePresence>
          <motion.div variants={GRID_CARDS_VARIANTS} initial='initial' animate='animate' className='grid sm:grid-cols-2 gap-[15px]'>
            {loading &&
              Array.from(Array(25).keys()).map((k, i) => (
                <UnitListCardSmSkeleton
                  key={`portal-unit-list-skeleton-${k}`}
                  index={i}
                  bgClasses='bg-surface border border-border min-w-full sm:min-w-[200px] lg:min-w-[250px]'
                  textClasses='text-text'
                  iconClasses='text-text'
                  bgSecondaryClasses='bg-surface-2'
                  textSecondaryClasses='text-text'
                  bgTertiaryClasses='bg-surface'
                />
              ))}
            {!loading &&
              reducedData.map((u: IPortalUnitSearchModel) => (
                <PortalUnitListCard
                  key={`portal-unit-list-${u.unitId}`}
                  bgClasses='!w-full !max-w-full bg-surface border border-border min-w-full sm:min-w-[200px] lg:min-w-[250px]'
                  textClasses='text-text'
                  iconClasses='text-text'
                  bgSecondaryClasses='bg-surface-2'
                  textSecondaryClasses='text-text'
                  bgTertiaryClasses='bg-surface'
                  unit={u}
                  buttonText='Select'
                  replacementButton={buttonReplacement(u)}
                  hideOrderButton
                  hideDetailsButton
                  navigateForward={() => {}}
                  callback={(unit) => {
                    filter.clear()
                    onDismiss(JSON.stringify(unit), ModalDismiss.Confirm)
                  }}
                />
              ))}
            {!loading && reducedData.length === 0 && (
              <NoResultsCard classes='sm:col-span-2 sm:mx-4'>You have no devices matching the selected filters.</NoResultsCard>
            )}{' '}
          </motion.div>
        </AnimatePresence>

        <IonInfiniteScroll
          onIonInfinite={async (ev) => {
            await unitsFetchNext()
            ev.target.complete()
          }}
        >
          <InfiniteLoader key='portal-unit-select-infinite-loader' loading={unitsIsFetching} />
        </IonInfiniteScroll>
      </ModalBodyInfiniteScrollable>
    </ModalContainer>
  )
}
